// =====================================================
// POLICIES ADMIN PAGE - Pólizas
// CRUD completo + botón ver tarifas + Import/Export
// Validación de código duplicado con error inline
// =====================================================

import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import {
  FileText,
  Plus,
  Search,
  RefreshCw,
  Edit2,
  Power,
  Loader2,
  Save,
  X,
  AlertCircle,
  Building2,
  Star,
  Receipt,
  Filter,
  FileSpreadsheet,
  Eye,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import { insuranceCompaniesApi } from '../../api/insurance-companies';
import { policiesApi } from '../../api/policies';
import { tariffsApi } from '../../api/tariffs';
import { policiesImportExport } from '../../api/import-export';
import type { InsuranceCompanySelect } from '../../api/insurance-companies';
import type { Policy } from '../../api/policies';
import type { Tariff } from '../../api/tariffs';
import { Modal, ModalFooter } from '../../components/ui/Modal';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ImportExportModal } from '../../components/ui/ImportExportModal';
import { toast } from '../../components/ui/Toast';
import { useNavigate } from 'react-router-dom';

interface PolicyFormData {
  insuranceCompanyId: string;
  code: string;
  name: string;
  description: string;
  isDefault: boolean;
}

export function PoliciesAdminPage() {
  const navigate = useNavigate();

  // Data state
  const [items, setItems] = useState<Policy[]>([]);
  const [filteredItems, setFilteredItems] = useState<Policy[]>([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompanySelect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterInsuranceCompanyId, setFilterInsuranceCompanyId] = useState<string>('');

  // Sorting and pagination
  type SortField = 'name' | 'code' | 'createdAt';
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Policy | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Tariffs modal state
  const [isTariffsModalOpen, setIsTariffsModalOpen] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null);
  const [policyTariffs, setPolicyTariffs] = useState<Tariff[]>([]);
  const [isLoadingTariffs, setIsLoadingTariffs] = useState(false);

  // Import/Export modal
  const [isImportExportOpen, setIsImportExportOpen] = useState(false);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    item: Policy | null;
    action: 'activate' | 'deactivate';
  }>({ isOpen: false, item: null, action: 'deactivate' });
  const [isProcessing, setIsProcessing] = useState(false);

  // Form
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PolicyFormData>({
    defaultValues: {
      insuranceCompanyId: '',
      code: '',
      name: '',
      description: '',
      isDefault: false,
    },
  });


  // Load insurance companies
  const loadInsuranceCompanies = useCallback(async () => {
    try {
      const result = await insuranceCompaniesApi.getForSelect();
      setInsuranceCompanies(result);
    } catch (err: any) {
      console.error('Error loading insurance companies:', err);
    }
  }, []);

  // Load policies
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await policiesApi.getAll(
        showInactive,
        filterInsuranceCompanyId || undefined
      );
      setItems(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar pólizas');
    } finally {
      setIsLoading(false);
    }
  }, [showInactive, filterInsuranceCompanyId]);

  useEffect(() => {
    loadInsuranceCompanies();
  }, [loadInsuranceCompanies]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter, sort and paginate items
  useEffect(() => {
    let result = items;

    // Filtrar por búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(query) ||
          item.name.toLowerCase().includes(query) ||
          item.insuranceCompanyName?.toLowerCase().includes(query)
      );
    }

    // Ordenar
    result = [...result].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortBy) {
        case 'code':
          aVal = a.code.toLowerCase();
          bVal = b.code.toLowerCase();
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt || 0).getTime();
          bVal = new Date(b.createdAt || 0).getTime();
          break;
        default:
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
      }
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredItems(result);
  }, [items, searchQuery, sortBy, sortOrder]);

  // Paginación calculada
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / limit);
  const paginatedItems = filteredItems.slice((page - 1) * limit, page * limit);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, showInactive, filterInsuranceCompanyId, sortBy, sortOrder, limit]);

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  // Handlers
  const handleCreate = () => {
    setEditingItem(null);
    setFormError(null);
    reset({
      insuranceCompanyId: filterInsuranceCompanyId || '',
      code: '',
      name: '',
      description: '',
      isDefault: false,
    });
    setIsModalOpen(true);
  };

  const handleEdit = (item: Policy) => {
    setEditingItem(item);
    setFormError(null);
    reset({
      insuranceCompanyId: item.insuranceCompanyId,
      code: item.code,
      name: item.name,
      description: item.description || '',
      isDefault: item.isDefault,
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (!isSaving) {
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
    }
  };

  const handleSave = async (formData: PolicyFormData) => {
    // Limpiar error previo
    setFormError(null);

    // Validar código duplicado dentro de la misma aseguradora
    const codeUpper = formData.code.toUpperCase().trim();
    const existingItem = items.find(
      (item) =>
        item.code.toUpperCase() === codeUpper &&
        item.insuranceCompanyId === formData.insuranceCompanyId &&
        item.id !== editingItem?.id
    );

    if (existingItem) {
      const insurerName = insuranceCompanies.find(ic => ic.id === formData.insuranceCompanyId)?.name || 'esta aseguradora';
      setFormError(`Ya existe una póliza con el código "${codeUpper}" en ${insurerName}`);
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        await policiesApi.update(editingItem.id, {
          insuranceCompanyId: formData.insuranceCompanyId,
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          isDefault: formData.isDefault,
        });
        toast.success('Actualizada', `Póliza "${formData.name}" actualizada`);
      } else {
        await policiesApi.create({
          insuranceCompanyId: formData.insuranceCompanyId,
          code: formData.code,
          name: formData.name,
          description: formData.description || undefined,
          isDefault: formData.isDefault,
        });
        toast.success('Creada', `Póliza "${formData.name}" creada`);
      }
      setIsModalOpen(false);
      setEditingItem(null);
      setFormError(null);
      loadData();
    } catch (err: any) {
      const codeUpper = formData.code.toUpperCase().trim();
      setFormError(`Ya existe una póliza con el código "${codeUpper}"`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = (item: Policy) => {
    setConfirmDialog({
      isOpen: true,
      item,
      action: item.isActive ? 'deactivate' : 'activate',
    });
  };

  const handleConfirmToggle = async () => {
    if (!confirmDialog.item) return;
    setIsProcessing(true);
    try {
      if (confirmDialog.action === 'deactivate') {
        await policiesApi.deactivate(confirmDialog.item.id);
        toast.success('Desactivada', `Póliza "${confirmDialog.item.name}" desactivada`);
      } else {
        await policiesApi.reactivate(confirmDialog.item.id);
        toast.success('Activada', `Póliza "${confirmDialog.item.name}" activada`);
      }
      loadData();
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudo cambiar el estado');
    } finally {
      setIsProcessing(false);
      setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' });
    }
  };

  // View tariffs handler
  const handleViewTariffs = async (policy: Policy) => {
    setSelectedPolicy(policy);
    setIsTariffsModalOpen(true);
    setIsLoadingTariffs(true);
    try {
      const tariffs = await tariffsApi.getAll(true, policy.id);
      setPolicyTariffs(tariffs);
    } catch (err: any) {
      toast.error('Error', err.message || 'No se pudieron cargar las tarifas');
    } finally {
      setIsLoadingTariffs(false);
    }
  };

  const handleCloseTariffsModal = () => {
    setIsTariffsModalOpen(false);
    setSelectedPolicy(null);
    setPolicyTariffs([]);
  };

  const handleGoToTariffs = () => {
    navigate('/admin/tarifas');
    handleCloseTariffsModal();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pólizas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestión de pólizas por aseguradora
          </p>
        </div>
      </div>

      {/* Toolbar - Nuevo diseño */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
        {/* Fila superior: Búsqueda y acciones principales */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Búsqueda y botón filtros */}
          <div className="flex items-center flex-1 max-w-2xl gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por código, nombre o aseguradora..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg text-sm font-medium transition-colors ${
                showFilters || filterInsuranceCompanyId
                  ? 'bg-blue-50 border-blue-200 text-blue-600' 
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>

          {/* Acciones */}
          <div className="flex items-center gap-2">
            <button
              onClick={loadData}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualizar"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setIsImportExportOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-blue-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Importar / Exportar
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-100 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nueva póliza
            </button>
          </div>
        </div>

        {/* Fila de filtros activos (chips) */}
        {(showInactive || filterInsuranceCompanyId || searchQuery) && (
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">
              Filtros activos:
            </span>
            
            {showInactive && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Estado: Incluye inactivos
                <button 
                  onClick={() => setShowInactive(false)}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}

            {filterInsuranceCompanyId && (
              <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                Aseguradora: {insuranceCompanies.find(ic => ic.id === filterInsuranceCompanyId)?.name || 'Seleccionada'}
                <button 
                  onClick={() => setFilterInsuranceCompanyId('')}
                  className="ml-1.5 hover:text-green-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            {searchQuery && (
              <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                Búsqueda: "{searchQuery}"
                <button 
                  onClick={() => setSearchQuery('')}
                  className="ml-1.5 hover:text-blue-900"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
            
            <button 
              onClick={() => {
                setShowInactive(false);
                setFilterInsuranceCompanyId('');
                setSearchQuery('');
              }}
              className="text-xs text-gray-400 hover:text-red-500 font-medium ml-2 underline underline-offset-2"
            >
              Limpiar todo
            </button>
          </div>
        )}

        {/* Panel de filtros */}
        {showFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-t border-gray-100 pt-3 gap-4">
            {/* Checkbox, aseguradora y ordenación */}
            <div className="flex items-center gap-6 flex-wrap">
              <label className="inline-flex items-center cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                  Mostrar inactivas
                </span>
              </label>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Aseguradora:</span>
                <select
                  value={filterInsuranceCompanyId}
                  onChange={(e) => setFilterInsuranceCompanyId(e.target.value)}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="">Todas</option>
                  {insuranceCompanies.map((ic) => (
                    <option key={ic.id} value={ic.id}>
                      {ic.code} - {ic.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Ordenar por:</span>
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field as SortField);
                    setSortOrder(order as 'asc' | 'desc');
                  }}
                  className="text-sm border-none bg-transparent py-0 pl-0 pr-8 font-medium text-gray-700 focus:ring-0 cursor-pointer hover:text-blue-600 transition-colors"
                >
                  <option value="name-asc">Nombre (A-Z)</option>
                  <option value="name-desc">Nombre (Z-A)</option>
                  <option value="code-asc">Código (A-Z)</option>
                  <option value="code-desc">Código (Z-A)</option>
                  <option value="createdAt-desc">Fecha (Más reciente)</option>
                  <option value="createdAt-asc">Fecha (Antiguo)</option>
                </select>
              </div>
            </div>

            {/* Items por página */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">Ver:</span>
              <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setLimit(10)}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 10 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  10
                </button>
                <button
                  onClick={() => setLimit(25)}
                  className={`px-3 py-1 text-xs font-semibold border-r border-gray-200 transition-colors ${
                    limit === 25 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  25
                </button>
                <button
                  onClick={() => setLimit(50)}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${
                    limit === 50 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                  }`}
                >
                  50
                </button>
              </div>
              <span className="text-sm text-gray-500">por página</span>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {paginatedItems.length === 0 && !isLoading ? (
          <div className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {searchQuery || filterInsuranceCompanyId ? 'Sin resultados' : 'No hay pólizas'}
            </h3>
            <p className="text-gray-500">
              {searchQuery || filterInsuranceCompanyId
                ? 'No se encontraron pólizas con ese criterio'
                : 'Crea tu primera póliza'}
            </p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Aseguradora
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Default
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Tarifas
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedItems.map((item) => (
                  <tr
                    key={item.id}
                    className={`hover:bg-gray-50 ${!item.isActive ? 'bg-gray-50/50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2.5 py-1 bg-purple-50 text-purple-700 text-xs font-semibold rounded-lg">
                        {item.code}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`font-medium ${
                          item.isActive ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        {item.name}
                      </span>
                      {item.description && (
                        <p className="text-xs text-gray-500 truncate max-w-xs">
                          {item.description}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          {item.insuranceCompanyName || item.insuranceCompanyCode}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {item.isDefault ? (
                        <Star className="h-5 w-5 text-amber-500 mx-auto fill-amber-500" />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleViewTariffs(item)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-100 transition-colors"
                        title="Ver tarifas vinculadas"
                      >
                        <Receipt className="h-3.5 w-3.5" />
                        {item.tariffsCount ?? 0}
                        <Eye className="h-3 w-3 ml-0.5" />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => handleToggleStatus(item)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                          item.isActive ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        role="switch"
                        aria-checked={item.isActive}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                            item.isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        >
                          <Power
                            className={`h-3 w-3 absolute top-1 left-1 ${
                              item.isActive ? 'text-blue-600' : 'text-gray-400'
                            }`}
                          />
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded"
                        title="Editar"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Mostrando {((page - 1) * limit) + 1} - {Math.min(page * limit, totalItems)} de {totalItems}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <span className="text-sm text-gray-600">
                  Página {page} de {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingItem ? 'Editar póliza' : 'Nueva póliza'}
        subtitle={editingItem ? `Editando: ${editingItem.name}` : 'Completa los datos'}
        size="md"
      >
        <form onSubmit={handleSubmit(handleSave)}>
          {/* Error banner dentro del modal */}
          {formError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-red-800">Error de validación</p>
                <p className="text-sm text-red-600 mt-0.5">{formError}</p>
              </div>
              <button
                type="button"
                onClick={() => setFormError(null)}
                className="flex-shrink-0 p-1 text-red-400 hover:text-red-600 rounded"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Aseguradora *
              </label>
              <select
                {...register('insuranceCompanyId', {
                  required: 'Selecciona una aseguradora',
                })}
                onChange={() => formError && setFormError(null)}
                className={`w-full px-4 py-2.5 border rounded-xl text-sm ${
                  errors.insuranceCompanyId ? 'border-red-300' : 'border-gray-200'
                }`}
              >
                <option value="">Seleccionar...</option>
                {insuranceCompanies.map((ic) => (
                  <option key={ic.id} value={ic.id}>
                    {ic.code} - {ic.name}
                  </option>
                ))}
              </select>
              {errors.insuranceCompanyId && (
                <p className="mt-1 text-xs text-red-500">
                  <AlertCircle className="h-3 w-3 inline mr-1" />
                  {errors.insuranceCompanyId.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código *
                </label>
                <input
                  type="text"
                  {...register('code', { 
                    required: 'El código es obligatorio',
                    maxLength: { value: 20, message: 'Máximo 20 caracteres' }
                  })}
                  onChange={() => formError && setFormError(null)}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm uppercase ${
                    errors.code || formError ? 'border-red-300 bg-red-50/50' : 'border-gray-200'
                  }`}
                  placeholder="POL001"
                />
                {errors.code && (
                  <p className="mt-1 text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {errors.code.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  {...register('name', { 
                    required: 'El nombre es obligatorio',
                    maxLength: { value: 100, message: 'Máximo 100 caracteres' }
                  })}
                  className={`w-full px-4 py-2.5 border rounded-xl text-sm ${
                    errors.name ? 'border-red-300' : 'border-gray-200'
                  }`}
                  placeholder="Nombre de la póliza"
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-500">
                    <AlertCircle className="h-3 w-3 inline mr-1" />
                    {errors.name.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción
              </label>
              <textarea
                {...register('description', {
                  maxLength: { value: 500, message: 'Máximo 500 caracteres' }
                })}
                rows={2}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm resize-none"
                placeholder="Descripción opcional..."
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                {...register('isDefault')}
                className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <Star className="h-4 w-4 text-amber-500" />
              <span className="text-sm text-gray-700">
                Póliza por defecto para esta aseguradora
              </span>
            </label>
          </div>

          <ModalFooter>
            <button
              type="button"
              onClick={handleCloseModal}
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl disabled:opacity-50 flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {editingItem ? 'Guardar' : 'Crear'}
                </>
              )}
            </button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Tariffs Modal */}
      <Modal
        isOpen={isTariffsModalOpen}
        onClose={handleCloseTariffsModal}
        title="Tarifas vinculadas"
        subtitle={selectedPolicy ? `${selectedPolicy.code} - ${selectedPolicy.name}` : ''}
        size="lg"
      >
        {isLoadingTariffs ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : policyTariffs.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">Sin tarifas</h3>
            <p className="text-gray-500 mb-4">Esta póliza no tiene tarifas vinculadas</p>
            <button
              onClick={handleGoToTariffs}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Crear tarifa
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {policyTariffs.map((tariff) => (
                <div
                  key={tariff.id}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    tariff.isActive
                      ? 'border-emerald-200 bg-emerald-50/50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">
                          {tariff.code}
                        </span>
                        <span className="font-medium text-gray-900">{tariff.name}</span>
                        {!tariff.isActive && (
                          <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-[10px] font-bold rounded uppercase">
                            Inactiva
                          </span>
                        )}
                      </div>
                      {tariff.description && (
                        <p className="text-sm text-gray-500 mt-1">{tariff.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Desde {formatDate(tariff.validFrom)}
                        </span>
                        {tariff.validTo && (
                          <span>Hasta {formatDate(tariff.validTo)}</span>
                        )}
                        <span className="flex items-center gap-1">
                          <Receipt className="h-3.5 w-3.5" />
                          {tariff.actsCount ?? 0} actos
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <ModalFooter>
              <button
                onClick={handleCloseTariffsModal}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cerrar
              </button>
              <button
                onClick={handleGoToTariffs}
                className="px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Ir a Tarifas
              </button>
            </ModalFooter>
          </>
        )}
      </Modal>

      {/* Import/Export Modal */}
      <ImportExportModal
        isOpen={isImportExportOpen}
        onClose={() => setIsImportExportOpen(false)}
        title="Pólizas"
        onExport={policiesImportExport.export}
        onDownloadTemplate={policiesImportExport.downloadTemplate}
        onPreview={policiesImportExport.preview}
        onImport={policiesImportExport.import}
        onImportSuccess={loadData}
        previewColumns={[
          { key: 'insuranceCompanyCode', label: 'Aseguradora' },
          { key: 'code', label: 'Código' },
          { key: 'name', label: 'Nombre' },
          { key: 'isDefault', label: 'Default' },
        ]}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() =>
          !isProcessing &&
          setConfirmDialog({ isOpen: false, item: null, action: 'deactivate' })
        }
        onConfirm={handleConfirmToggle}
        isLoading={isProcessing}
        variant={confirmDialog.action === 'deactivate' ? 'warning' : 'info'}
        title={
          confirmDialog.action === 'deactivate'
            ? '¿Desactivar póliza?'
            : '¿Activar póliza?'
        }
        message={
          confirmDialog.action === 'deactivate'
            ? `La póliza "${confirmDialog.item?.name}" será desactivada.`
            : `La póliza "${confirmDialog.item?.name}" será reactivada.`
        }
        confirmText={confirmDialog.action === 'deactivate' ? 'Desactivar' : 'Activar'}
      />
    </div>
  );
}

export default PoliciesAdminPage;
